import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { DashboardForm } from './DashboardForm'
import { DashboardViewCount } from './DashboardViewCount'

interface Dashboard {
  id: string
  name: string
  slug: string
  description: string | null
  is_published: boolean
  is_default: boolean
  created_at: string
  component_count?: number
}

interface DashboardListProps {
  onEditDashboard: (dashboardId: string) => void
}

export function DashboardList({ onEditDashboard }: DashboardListProps) {
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  useEffect(() => {
    loadDashboards()
  }, [])

  const loadDashboards = async () => {
    try {
      setLoading(true)
      setError(null)

      // @ts-ignore
      const { data, error: fetchError } = await supabase
        .from('dashboards')
        .select(`
          id,
          name,
          slug,
          description,
          is_published,
          show_tematic_view,
          is_default,
          created_at,
          dashboard_components(count)
        `)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      // Process component counts
      const processedData = (data || []).map((d: any) => ({
        ...d,
        component_count: d.dashboard_components?.[0]?.count || 0,
      }))

      setDashboards(processedData)
    } catch (err) {
      console.error('Error cargando dashboards:', err)
      setError('Error al cargar los dashboards')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string, name: string, isDefault: boolean, isPublished: boolean) => {
    // Prevent deleting default published dashboard
    if (isDefault && isPublished) {
      if (!confirm('Este es el dashboard predeterminado publicado. ¿Estás seguro de eliminarlo? Los usuarios no verán ningún dashboard en la landing hasta que establezcas otro como predeterminado.')) {
        return
      }
    } else if (!confirm(`¿Estás seguro de eliminar el dashboard "${name}"?`)) {
      return
    }

    try {
      // @ts-ignore
      const { error: deleteError } = await supabase
        .from('dashboards')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      await loadDashboards()
    } catch (err) {
      console.error('Error eliminando dashboard:', err)
      alert('Error al eliminar el dashboard')
    }
  }

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    try {
      const query = supabase.from('dashboards')
      const { error: updateError } = await query
        // @ts-ignore
        .update({ is_published: !currentStatus })
        .eq('id', id)

      if (updateError) throw updateError

      await loadDashboards()
    } catch (err) {
      console.error('Error actualizando dashboard:', err)
      alert('Error al actualizar el dashboard')
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      // Usar la función RPC que supera las restricciones de RLS
      // @ts-ignore - RPC function not in generated types
      const { error: rpcError } = await supabase.rpc('set_dashboard_as_default', {
        dashboard_id: id
      })

      if (rpcError) {
        console.error('Error en RPC set_dashboard_as_default:', rpcError)
        throw rpcError
      }

      await loadDashboards()
    } catch (err) {
      console.error('Error estableciendo default:', err)
      alert('Error al establecer dashboard predeterminado')
    }
  }

  const handleEdit = (id: string) => {
    setEditingId(id)
    setShowForm(true)
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingId(null)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingId(null)
    loadDashboards()
  }

  const filteredDashboards = dashboards.filter(dash => {
    if (filter === 'published' && !dash.is_published) return false
    if (filter === 'draft' && dash.is_published) return false
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboards...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadDashboards}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Dashboards</h1>
          <p className="text-gray-600 mt-1">
            Crea y administra dashboards públicos con componentes dinámicos
          </p>
        </div>
        <button
          onClick={() => {
            setEditingId(null)
            setShowForm(true)
          }}
          className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Crear Dashboard
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos ({dashboards.length})
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'published'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Publicados ({dashboards.filter(d => d.is_published).length})
          </button>
          <button
            onClick={() => setFilter('draft')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'draft'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Borradores ({dashboards.filter(d => !d.is_published).length})
          </button>
        </div>
      </div>

      {/* Dashboards Grid */}
      {filteredDashboards.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No hay dashboards
          </h3>
          <p className="text-gray-500 mb-6">
            {filter === 'all'
              ? 'Aún no has creado ningún dashboard. ¡Crea tu primer dashboard ahora!'
              : `No hay dashboards ${filter === 'published' ? 'publicados' : 'en borrador'}`}
          </p>
          <button
            onClick={() => {
              setEditingId(null)
              setShowForm(true)
            }}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
          >
            Crear Primer Dashboard
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDashboards.map((dashboard) => (
            <div
              key={dashboard.id}
              className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-3xl">📊</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{dashboard.name}</h3>
                      {dashboard.is_default && (
                        <span className="inline-block mt-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                          ⭐ Predeterminado
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    {dashboard.is_published ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Publicado
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                        Borrador
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {dashboard.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {dashboard.description}
                  </p>
                )}

                {/* Details */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Componentes:</span>
                    <span className="font-medium text-gray-900">{dashboard.component_count || 0}</span>
                  </div>
                  {dashboard.is_published && (
                    <DashboardViewCount dashboardId={dashboard.id} />
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Slug:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                      {dashboard.slug}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Creado:</span>
                    <span className="font-medium text-gray-900">
                      {new Date(dashboard.created_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEditDashboard(dashboard.id)}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                    >
                      Editar Componentes
                    </button>
                    <button
                      onClick={() => handleEdit(dashboard.id)}
                      className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      Configurar
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTogglePublish(dashboard.id, dashboard.is_published)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        dashboard.is_published
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {dashboard.is_published ? 'Despublicar' : 'Publicar'}
                    </button>
                    <button
                      onClick={() => handleSetDefault(dashboard.id)}
                      disabled={dashboard.is_default}
                      className="flex-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {dashboard.is_default ? 'Es Default' : 'Set Default'}
                    </button>
                    <button
                      onClick={() => handleDelete(dashboard.id, dashboard.name, dashboard.is_default, dashboard.is_published)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {dashboards.length > 0 && (
        <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Estadísticas</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{dashboards.length}</div>
              <div className="text-sm text-gray-600">Total Dashboards</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {dashboards.filter(d => d.is_published).length}
              </div>
              <div className="text-sm text-gray-600">Publicados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {dashboards.filter(d => d.is_default).length}
              </div>
              <div className="text-sm text-gray-600">Predeterminado</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {dashboards.reduce((sum, d) => sum + (d.component_count || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Componentes</div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <DashboardForm
          dashboardId={editingId}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}
